import { useMutation, UseMutationOptions } from "@tanstack/react-query";
import axiosInstance from "axiosConfig";
import { Toast } from "shared";
import { TOAST_TYPES_ENUM } from "enums";
import { queryClient } from "App";
import { StoreAnalyzeType } from "types/AnalyzeTypes";

export const useStoreAnalyze = (
  options?: UseMutationOptions<any, Error, StoreAnalyzeType>
) => {
  return useMutation({
    ...options,
    mutationKey: ["storeAnalyze"],
    mutationFn: async (data) => {
      const formData = new FormData();
      formData.append("companyName", data.companyName);
      formData.append("status", data.status);
      formData.append("nip", data.nip);
      formData.append("deadlineAt", data.deadlineAt);
      formData.append("description", data.description || "");
      
      if (data.files && data.files.length > 0) {
        data.files.forEach((file, index) => {
          formData.append(`files`, file);
        });
      }

      return axiosInstance.post("/analyzes", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }).then((res) => res.data);
    },
    onMutate: (variables) => {
      Toast({
        message: "Trwa dodawanie analizy...",
        type: TOAST_TYPES_ENUM.LOADING,
        id: "store-analyze",
      });
      if (options && options.onMutate) {
        return options.onMutate(variables);
      }
    },
    onSuccess: (data, variables, context) => {
      Toast({
        message: "Analiza dodana pomyślnie.",
        type: TOAST_TYPES_ENUM.SUCCESS,
        id: "store-analyze",
      });
      queryClient.invalidateQueries({ queryKey: ["analyzes"] });
      if (options && options.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
    onError: (error: any, variables, context) => {
      const data = error?.data ?? error?.response?.data;

      if (data?.message === "Some files are invalid" && data?.errors) {
        const allowedExtensions = ["png", "jpg", "doc", "docx", "pdf"];
        const entries = Array.isArray(data.errors) ? data.errors : [];

        const flatIssues = entries.flatMap((e: any) =>
          Array.isArray(e?.errors) ? e.errors : []
        );

        const sizeErrors = flatIssues.filter((err: any) => err?.type === "size");

        if (sizeErrors.length > 0) {
          const fileNames = sizeErrors
            .map((err: any) => err?.clientName)
            .filter(Boolean)
            .join(", ");
          Toast({
            message: fileNames
              ? `Następujące pliki są zbyt duże (max 20MB): ${fileNames}`
              : "Niektóre pliki są zbyt duże (max 20MB).",
            type: TOAST_TYPES_ENUM.ERROR,
            id: "store-analyze",
          });
        } else {
          const invalidExtItems = entries.filter(
            (e: any) =>
              typeof e?.extension === "string" &&
              !allowedExtensions.includes(e.extension.toLowerCase())
          );

          if (invalidExtItems.length > 0 || flatIssues.length === 0) {
            Toast({
              message: "Dozwolone formaty plików: .png, .jpg, .doc, .docx, .pdf",
              type: TOAST_TYPES_ENUM.ERROR,
              id: "store-analyze",
            });
          } else {
            Toast({
              message: "Błąd podczas walidacji plików.",
              type: TOAST_TYPES_ENUM.ERROR,
              id: "store-analyze",
            });
          }
        }
      } else {
        Toast({
          message: "Błąd podczas dodawania analizy.",
          type: TOAST_TYPES_ENUM.ERROR,
          id: "store-analyze",
        });
      }

      if (options && options.onError) {
        options.onError(error, variables, context);
      }
    },
  });
};

export default useStoreAnalyze;
